import type { MouseEvent } from 'react'
import { Link } from '@tanstack/react-router'

import { IdCell } from '#/shared/ui/table-cells'
import type { IdCellBadge } from '#/shared/ui/table-cells'

import { useOpenShipmentDetail } from '../lib/use-open-shipment-detail'

interface ShipmentIdCellProps {
  shipmentId: string
  code: string
  badges?: IdCellBadge[]
  to?: string
  params?: Record<string, string>
}

function isModifiedClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return (
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    event.button !== 0
  )
}

function ShipmentIdLink({
  shipmentId,
  code,
  badges,
  to,
  params,
}: ShipmentIdCellProps & { to: string; params: Record<string, string> }) {
  const openShipmentDetail = useOpenShipmentDetail()

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // Keep the row's sidebar click from firing when the id link is used.
    event.stopPropagation()
    if (isModifiedClick(event)) return
    event.preventDefault()
    void openShipmentDetail({ shipmentId, to, params })
  }

  return (
    <Link
      to={to as never}
      params={params as never}
      search={((prev: Record<string, unknown>) => prev) as never}
      onClick={handleClick}
      className="group block"
    >
      <IdCell id={code} badges={badges} />
    </Link>
  )
}

export function ShipmentIdCell({
  shipmentId,
  code,
  badges,
  to,
  params,
}: ShipmentIdCellProps) {
  if (!to || !params) return <IdCell id={code} badges={badges} />
  return (
    <ShipmentIdLink
      shipmentId={shipmentId}
      code={code}
      badges={badges}
      to={to}
      params={params}
    />
  )
}
